(() => {
  const DISCOUNT_CODE = 'Better Together - 30% off';
  const STATE_SELECTOR = '#better-together-promo-state';

  let syncTimer = null;
  let syncInProgress = false;
  let internalCartUpdate = false;

  const cartRoot =
    window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';

  /**
   * Get the latest eligibility directly from Shopify.
   *
   * We fetch /cart instead of trusting the existing DOM because
   * Horizon / AJAX cart updates can leave old Liquid markup in the
   * page briefly after a product is removed.
   */
  async function getCurrentEligibility() {
    try {
      const response = await fetch(`${cartRoot}cart`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Unable to load cart. HTTP ${response.status}`
        );
      }

      const html = await response.text();

      const parser = new DOMParser();
      const documentFromCart = parser.parseFromString(
        html,
        'text/html'
      );

      const promoState =
        documentFromCart.querySelector(STATE_SELECTOR);

      /*
       * If the state element doesn't exist, the cart is most
       * likely empty.
       */
      if (!promoState) {
        return false;
      }

      return promoState.dataset.qualified === 'true';

    } catch (error) {
      console.error(
        '[Better Together] Eligibility check failed:',
        error
      );

      return null;
    }
  }


  /**
   * Apply the Better Together code to Shopify's actual cart.
   */
  async function applyDiscount() {
    internalCartUpdate = true;

    try {
      const response = await fetch(
        `${cartRoot}cart/update.js`,
        {
          method: 'POST',
          credentials: 'same-origin',

          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },

          body: JSON.stringify({
            discount: DISCOUNT_CODE
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          `Unable to apply discount. HTTP ${response.status}`
        );
      }

      const cart = await response.json();

      console.log(
        '[Better Together] Discount applied:',
        DISCOUNT_CODE,
        cart
      );

      return cart;

    } catch (error) {

      console.error(
        '[Better Together] Unable to apply discount:',
        error
      );

      return null;

    } finally {

      internalCartUpdate = false;
    }
  }


  /**
   * Remove discount from Shopify's actual cart/session.
   *
   * This is the important part.
   *
   * discount: ''
   *
   * tells Shopify to remove the persisted discount codes from
   * the cart. Therefore the old code will NOT appear at checkout.
   */
  async function removeDiscount() {
    internalCartUpdate = true;

    try {
      const response = await fetch(
        `${cartRoot}cart/update.js`,
        {
          method: 'POST',
          credentials: 'same-origin',

          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },

          body: JSON.stringify({
            discount: ''
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          `Unable to remove discount. HTTP ${response.status}`
        );
      }

      const cart = await response.json();

      console.log(
        '[Better Together] Discount removed.',
        cart
      );

      return cart;

    } catch (error) {

      console.error(
        '[Better Together] Unable to remove discount:',
        error
      );

      return null;

    } finally {

      internalCartUpdate = false;
    }
  }


  /**
   * Synchronize Shopify's discount with actual cart contents.
   */
  async function syncBetterTogetherDiscount() {

    if (syncInProgress) {
      return;
    }

    syncInProgress = true;

    try {

      const qualified = await getCurrentEligibility();

      /*
       * Don't change anything if we couldn't determine
       * eligibility because of a network error.
       */
      if (qualified === null) {
        return;
      }

      if (qualified) {

        console.log(
          '[Better Together] Cart qualifies.'
        );

        await applyDiscount();

      } else {

        console.log(
          '[Better Together] Cart no longer qualifies.'
        );

        await removeDiscount();
      }

    } finally {

      syncInProgress = false;
    }
  }


  /**
   * Debounce repeated cart events.
   */
  function scheduleSync(delay = 150) {

    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      syncBetterTogetherDiscount();
    }, delay);
  }


  /**
   * Initial page load.
   */
  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      () => scheduleSync(0)
    );

  } else {

    scheduleSync(0);
  }


  /**
   * Quantity changes.
   */
  document.addEventListener(
    'change',
    (event) => {

      if (
        event.target.matches(
          `
            input[name="updates[]"],
            .quantity__input,
            [data-quantity-input]
          `
        )
      ) {

        /*
         * Give Shopify time to finish the quantity request.
         */
        scheduleSync(500);
      }
    }
  );


  /**
   * Remove buttons.
   */
  document.addEventListener(
    'click',
    (event) => {

      const removeButton = event.target.closest(
        `
          cart-remove-button,
          .cart-remove-button,
          [data-cart-remove],
          a[href*="/cart/change"]
        `
      );

      if (removeButton) {

        /*
         * Allow Shopify's remove request to finish first.
         */
        scheduleSync(500);
      }
    }
  );


  /**
   * Detect AJAX cart requests.
   *
   * Horizon and most modern Shopify themes use fetch()
   * for cart updates.
   */
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function (...args) {

    const response = await originalFetch(...args);

    try {

      let requestUrl = '';

      if (typeof args[0] === 'string') {

        requestUrl = args[0];

      } else if (args[0] && args[0].url) {

        requestUrl = args[0].url;
      }

      const isCartMutation =
        /\/cart\/(add|change|update|clear)(\.js)?/i.test(
          requestUrl
        );

      /*
       * Ignore the cart/update.js request that THIS script
       * generated to avoid an infinite loop.
       */
      if (
        isCartMutation &&
        !internalCartUpdate
      ) {

        scheduleSync(200);
      }

    } catch (error) {

      console.warn(
        '[Better Together] Unable to inspect cart request:',
        error
      );
    }

    return response;
  };


  /**
   * Browser back button / return from checkout.
   *
   * This matters because browsers can restore the cart from
   * their back-forward cache.
   */
  window.addEventListener(
    'pageshow',
    () => scheduleSync(100)
  );

})();